import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL, EXTERNAL_BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'
import Loading from '../element/Loading'
import InvoiceEditModal from '../Component/InvoiceEditModal'
import InvoiceScanModal from '../Component/InvoiceScanModal'
import logo from '../imgs/nextlead-logo.png'
import { Plus, Trash, Printer, Eye, PlusCircle, X, FileText, Edit, Trash2, Search, ScanBarcode } from 'lucide-react'

const emptyInvoiceItem = {
  product_id: '',
  product_variant_id: '',
  product_name: '',
  variant_reference: '',
  quantity: 1,
  unit_price: '',
  line_total: 0,
}

export default function InvoicesPage() {
  const { t, lang } = useTranslation()
  const { can } = useAuth()
  const [clientName, setClientName] = useState('')
  const [invoiceCode, setInvoiceCode] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [status, setStatus] = useState('not_yet')
  const [remise, setRemise] = useState('')
  const [logoFile, setLogoFile] = useState(null)
  const [products, setProducts] = useState([])
  const [items, setItems] = useState([{ ...emptyInvoiceItem }])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedInvoice, setSavedInvoice] = useState(null)
  const [errors, setErrors] = useState({})
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null)
  const [openProductDropdown, setOpenProductDropdown] = useState(null)
  
  // Pagination states
  const [invoices, setInvoices] = useState([])
  const [loadingInvoices, setLoadingInvoices] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [totalInvoices, setTotalInvoices] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  
  // Modal states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentNote, setPaymentNote] = useState('')
  const [savingPayment, setSavingPayment] = useState(false)
  const [selectedInvoiceHistory, setSelectedInvoiceHistory] = useState(null)
  const [editModalInvoice, setEditModalInvoice] = useState(null)
  const [deleteConfirmInvoice, setDeleteConfirmInvoice] = useState(null)
  const [deletingInvoice, setDeletingInvoice] = useState(false)
  const [removingPayment, setRemovingPayment] = useState(false)
  const [paymentToRemove, setPaymentToRemove] = useState(null)
  const [scanModalOpen, setScanModalOpen] = useState(false)
  
  const [barcodeInput, setBarcodeInput] = useState('')
  const token = Cookies.get('WEB_SSR')
  const printRef = useRef()
  const barcodeInputRef = useRef(null)
  const navigate = useNavigate()

  // Fetch invoices function
  const fetchInvoices = async (page = 1, perPage = 10, search = '') => {
    // setLoadingInvoices(true)
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
      if (search.trim()) params.append('search', search.trim())
      const response = await axios.get(`${BASE_URL}/api/invoices?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })

      if (response.data.success) {
        const invoicesWithPaid = response.data.data.data.map(invoice => {
          const paid = (invoice.payments || []).reduce((sum, p) => sum + Number(p.amount), 0)
          return {
            ...invoice,
            paid: paid,
            balance: Math.max(invoice.total - paid, 0)
          }
        })
        setInvoices(invoicesWithPaid)
        setCurrentPage(response.data.data.current_page)
        setLastPage(response.data.data.last_page)
        setTotalInvoices(response.data.data.total)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingInvoices(false)
    }
  }

  const fetchProducts = async () => {
    setLoadingProducts(true)
    try {
      const response = await axios.get(`${BASE_URL}/api/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        params: { per_page: 100000 }
      })

      if (response.data.success) {
        setProducts(response.data.data.data || [])
      }
    } catch (error) {
      console.error(error)
      toast.error(t('invoices.error.loading_products'))
    } finally {
      setLoadingProducts(false)
    }
  }
  
  useEffect(() => {
    if (!token) {
      navigate('/')
      return
    }

    fetchProducts()
    fetchInvoices(currentPage, perPage, searchQuery)
  }, [token, navigate, t])

  const invoiceSubtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0
      const unitPrice = Number(item.unit_price) || 0
      return sum + quantity * unitPrice
    }, 0)
  }, [items])

  const invoiceTotal = useMemo(() => {
    const remiseVal = Number(remise) || 0
    return Math.max(invoiceSubtotal - remiseVal, 0)
  }, [invoiceSubtotal, remise])

  const handleAddRow = () => {
    setItems((current) => [...current, { ...emptyInvoiceItem }])
  }

  const handleRemoveRow = (index) => {
    setItems((current) => current.filter((_, i) => i !== index))
  }

  const updateItem = (index, updates) => {
    setItems((current) => current.map((item, i) => {
      if (i !== index) return item
      const next = { ...item, ...updates }
      const quantity = Number(next.quantity) || 0
      const unitPrice = Number(next.unit_price) || 0
      next.line_total = parseFloat((quantity * unitPrice).toFixed(2))
      return next
    }))
  }

  const handleProductChange = (index, productId) => {
    const product = products.find((product) => product.id === Number(productId))
    if (!product) {
      updateItem(index, {
        product_id: '',
        product_variant_id: '',
        product_name: '',
        variant_reference: '',
        unit_price: '',
      })
      return
    }

    updateItem(index, {
      product_id: product.id,
      product_variant_id: '',
      product_name: product.name,
      variant_reference: '',
      unit_price: product.price,
    })
  }

  const handleVariantChange = (index, variantId) => {
    setItems((current) => current.map((item, i) => {
      if (i !== index) return item
      const product = products.find((product) => product.id === Number(item.product_id))
      const variant = product?.variants?.find((variant) => variant.id === Number(variantId))
      if (!variant) {
        return {
          ...item,
          product_variant_id: '',
          variant_reference: '',
        }
      }

      return {
        ...item,
        product_variant_id: variant.id,
        variant_reference: variant.color && variant.size ? `${variant.color} / ${variant.size}` : variant.color || variant.size || '',
      }
    }))
  }

  const handleQuantityChange = (index, value) => {
    const quantity = Number(value) || 0
    updateItem(index, { quantity })
  }

  const handleUnitPriceChange = (index, value) => {
    const unit_price = value === '' ? '' : Number(value)
    updateItem(index, { unit_price })
  }

  const handleBarcodeScan = (e) => {
    e.preventDefault()
    const barcode = barcodeInput.trim()
    if (!barcode) return

    let matchedProduct = null
    let matchedVariant = null

    for (const product of products) {
      if (product.barcode === barcode) {
        matchedProduct = product
        break
      }
      if (product.variants) {
        for (const variant of product.variants) {
          if (variant.barcode === barcode) {
            matchedProduct = product
            matchedVariant = variant
            break
          }
        }
        if (matchedProduct) break
      }
    }

    if (!matchedProduct) {
      toast.error(t('invoices.barcode_not_found'))
      setBarcodeInput('')
      barcodeInputRef.current?.focus()
      return
    }

    const variantReference = matchedVariant
      ? (matchedVariant.color && matchedVariant.size ? `${matchedVariant.color} / ${matchedVariant.size}` : matchedVariant.color || matchedVariant.size || '')
      : ''

    setItems((current) => [...current, {
      product_id: matchedProduct.id,
      product_variant_id: matchedVariant ? matchedVariant.id : '',
      product_name: matchedProduct.name,
      variant_reference: variantReference,
      quantity: 1,
      unit_price: matchedProduct.price,
      line_total: matchedProduct.price,
    }])

    setBarcodeInput('')
    toast.success(`${matchedProduct.name}${variantReference ? ' — ' + variantReference : ''} ${t('invoices.item_added')}`)
    barcodeInputRef.current?.focus()
  }

  const handleAddScannedItem = (item) => {
    setItems((current) => {
      const filtered = current.filter(
        (i) => i.product_id || i.product_name
      )
      return [...filtered, { ...emptyInvoiceItem, ...item }]
    })
  }

  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(logoFile)
    setLogoPreviewUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [logoFile])

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null
    setLogoFile(file)
  }

  const validateForm = () => {
    const validationErrors = {}
    if (!clientName.trim()) validationErrors.clientName = t('invoices.validation.client_name')
    if (!clientPhone.trim()) validationErrors.clientPhone = t('invoices.validation.client_phone')

    const itemErrors = items.map((item) => {
      const rowErrors = {}
      if (!item.product_id && !item.product_name.trim()) {
        rowErrors.product = t('invoices.validation.product')
      }
        const product = products.find((product) => product.id === Number(item.product_id))
        if (product?.variants?.length > 0 && !item.product_variant_id) {
          rowErrors.variant = t('invoices.validation.variant_required')
        }
      if (!item.quantity || Number(item.quantity) <= 0) {
        rowErrors.quantity = t('invoices.validation.quantity')
      }
      return rowErrors
    })

    if (itemErrors.some((row) => Object.keys(row).length > 0)) {
      validationErrors.items = itemErrors
    }

    setErrors(validationErrors)
    return Object.keys(validationErrors).length === 0
  }

  const handlePrint = () => {
    const printElement = printRef.current
    if (!printElement) {
      toast.error(t('invoices.error.print'))
      return
    }

    const printWindow = window.open('')
    if (!printWindow) {
      toast.error(t('invoices.error.print'))
      return
    }

    const headHtml = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join('')

    const title = savedInvoice?.invoice_code || invoiceCode || 'invoice'
    const bodyHtml = printElement.outerHTML

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <base href="${window.location.origin}">
          <title>${title}</title>
          ${headHtml}
          <style>
            body { margin: 20px; font-family: sans-serif; background: white; }
            .print-hidden { display: none !important; }
          </style>
        </head>
        <body>
          ${bodyHtml}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  const handleSaveInvoice = async () => {
    if (!validateForm()) {
      toast.error(t('invoices.error.validation'))
      return
    }

    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('client_name', clientName)
      formData.append('client_phone', clientPhone)
      formData.append('status', status)
      formData.append('remise', remise || '0')
      formData.append('invoice_code', invoiceCode)
      if (logoFile) {
        formData.append('logo', logoFile)
      }
      formData.append('items', JSON.stringify(items.map((item) => ({
        product_id: item.product_id || null,
        product_variant_id: item.product_variant_id || null,
        product_name: item.product_name || '',
        quantity: Number(item.quantity) || 0,
        unit_price: item.unit_price === '' ? null : Number(item.unit_price),
      }))))

      const response = await axios.post(`${BASE_URL}/api/invoices`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.success) {
        setSavedInvoice(response.data.data)
        toast.success(t('invoices.saved'))
        setClientName('')
        setClientPhone('')
        setStatus('not_yet')
        setInvoiceCode('')
        setRemise('')
        setLogoFile(null)
        setItems([{ ...emptyInvoiceItem }])
        
        // Refresh the invoices list
        fetchInvoices(currentPage, perPage, searchQuery)
      } else {
        toast.error(response.data.message || t('invoices.error.general'))
      }
    } catch (error) {
      console.error(error)
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const responseErrors = error.response.data.errors
        const formattedErrors = {}

        Object.entries(responseErrors).forEach(([key, messages]) => {
          const message = Array.isArray(messages) ? messages[0] : messages

          if (key.startsWith('items.')) {
            const [, index, field] = key.split('.')
            if (!formattedErrors.items) {
              formattedErrors.items = []
            }
            const itemIndex = Number(index)
            formattedErrors.items[itemIndex] = formattedErrors.items[itemIndex] || {}
            formattedErrors.items[itemIndex][field] = message
          } else {
            formattedErrors[key] = message
          }
        })

        setErrors(formattedErrors)
        toast.error(t('invoices.error.validation'))
        return
      }
      toast.error(error.response?.data?.message || t('invoices.error.general'))
    } finally {
      setSaving(false)
    }
  }

  const handleAddPayment = (invoice) => {
    setSelectedInvoice(invoice)
    setPaymentAmount(invoice.balance.toFixed(2))
    setPaymentNote('')
    setPaymentModalOpen(true)
  }

  const handleSubmitPayment = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast.error(t('invoices.error.payment_amount'))
      return
    }

    setSavingPayment(true)
    try {
      await axios.post(
        `${BASE_URL}/api/invoices/${selectedInvoice.id}/payments`,
        {
          amount: Number(paymentAmount),
          note: paymentNote,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      )

      toast.success(t('invoices.payment.success'))
      
      // Refresh the invoices list
      fetchInvoices(currentPage, perPage, searchQuery)
      
      setPaymentModalOpen(false)
      setPaymentAmount('')
      setPaymentNote('')
      setSelectedInvoice(null)
    } catch (error) {
      console.error(error)
      toast.error(error.response?.data?.message || t('invoices.error.payment'))
    } finally {
      setSavingPayment(false)
    }
  }

  const handleViewHistory = (invoice) => {
    setSelectedInvoiceHistory(invoice)
    setHistoryModalOpen(true)
  }

  const handleDeleteInvoice = async () => {
    if (!deleteConfirmInvoice) return
    setDeletingInvoice(true)
    try {
      await axios.delete(`${BASE_URL}/api/invoices/${deleteConfirmInvoice.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      toast.success(t('invoices.delete_success'))
      setDeleteConfirmInvoice(null)
      fetchInvoices(currentPage, perPage)
    } catch (error) {
      console.error(error)
      toast.error(error.response?.data?.message || t('invoices.error.general'))
    } finally {
      setDeletingInvoice(false)
    }
  }

  const handleRemovePayment = async () => {
    if (!selectedInvoiceHistory || !paymentToRemove) return
    setRemovingPayment(true)
    try {
      const response = await axios.delete(
        `${BASE_URL}/api/invoices/${selectedInvoiceHistory.id}/payments/${paymentToRemove.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      )
      toast.success(t('invoices.payment_removed'))
      const updated = { ...selectedInvoiceHistory, payments: response.data.data.payments }
      setSelectedInvoiceHistory(updated)
      setPaymentToRemove(null)
      fetchInvoices(currentPage, perPage, searchQuery)
    } catch (error) {
      console.error(error)
      toast.error(error.response?.data?.message || t('invoices.error.general'))
    } finally {
      setRemovingPayment(false)
    }
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= lastPage && page !== currentPage) {
      setCurrentPage(page)
      fetchInvoices(page, perPage, searchQuery)
    }
  }

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage)
    setCurrentPage(1)
    fetchInvoices(1, newPerPage, searchQuery)
  }

  const handleSearchChange = (value) => {
    setSearchQuery(value)
    setCurrentPage(1)
    fetchInvoices(1, perPage, value)
  }

  // Product Dropdown Component
  const ProductDropdown = ({ index, item }) => {
    const [open, setOpen] = useState(false)
    const [style, setStyle] = useState({})
    const [search, setSearch] = useState('')
    const btnRef = useRef(null)
    const searchRef = useRef(null)
    const selected = products.find(p => p.id === Number(item.product_id))

    const filteredProducts = useMemo(() => {
      if (!search.trim()) return products
      const query = search.trim().toLowerCase()
      return products.filter((product) => product.name?.toLowerCase().includes(query))
    }, [search, products])

    const updatePosition = () => {
      const rect = btnRef.current?.getBoundingClientRect()
      if (rect) {
        setStyle({
          position: 'fixed',
          top: rect.bottom + 4 + 'px',
          left: rect.left + 'px',
          width: Math.max(rect.width, 280) + 'px',
        })
      }
    }

    useEffect(() => {
      if (!open) return

      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      const timeoutId = setTimeout(() => searchRef.current?.focus(), 0)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
        clearTimeout(timeoutId)
      }
    }, [open])

    useEffect(() => {
      if (!open) setSearch('')
    }, [open])

    useEffect(() => {
      const handler = (e) => {
        if (btnRef.current && !btnRef.current.closest('[data-dropdown]')?.contains(e.target)) setOpen(false)
      }
      document.addEventListener('mousedown', handler)
      return () => document.removeEventListener('mousedown', handler)
    }, [])

    const getImageUrl = (product) => {
      if (!product.image) return null
      return product.source_id
        ? `${EXTERNAL_BASE_URL}/storage/${product.image}`
        : `${BASE_URL}/storage/${product.image}`
    }

    return (
      <div data-dropdown className="relative">
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-left text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          {selected?.image && (
            <img src={getImageUrl(selected)} alt="" className="h-6 w-6 rounded object-cover shrink-0" />
          )}
          <span className="truncate flex-1">
            {selected ? `${selected.name} — ${selected.price} / ${selected.qty} ${t('table.pcs')}` : t('invoices.select_product')}
          </span>
          <svg className={`h-4 w-4 shrink-0 transition ${open ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
        </button>
        {open && (
          <div style={style} className="z-50 max-w-[15%] max-h-80 overflow-hidden flex flex-col rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="sticky top-0 border-b border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}
                placeholder={t('invoices.search_product') || t('invoices.select_product')}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="overflow-auto">
              <button
                type="button"
                onClick={() => { handleProductChange(index, ''); setOpen(false) }}
                className="flex w-full items-center px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {t('invoices.select_product')}
              </button>
              {filteredProducts.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('invoices.no_products_found') || t('invoices.no_invoices')}
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => { handleProductChange(index, product.id); setOpen(false) }}
                    className={`flex w-full items-center text-start gap-3 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${Number(item.product_id) === product.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    {product.image ? (
                      <img src={getImageUrl(product)} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
                    ) : (
                      <div className="h-8 w-8 shrink-0 rounded bg-gray-200 dark:bg-gray-700" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{product.price} MAD / {product.qty} {t('table.pcs')}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Pagination Component
  const PaginationControls = () => {
    const getPageNumbers = () => {
      const pages = []
      const maxVisible = 10
      
      if (lastPage <= maxVisible) {
        for (let i = 1; i <= lastPage; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        
        let startPage = Math.max(2, currentPage - 4)
        let endPage = Math.min(lastPage - 1, currentPage + 5)
        
        if (currentPage <= 6) {
          endPage = 9
        }
        
        if (currentPage >= lastPage - 5) {
          startPage = lastPage - 8
        }
        
        if (startPage > 2) {
          pages.push('...')
        }
        
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i)
        }
        
        if (endPage < lastPage - 1) {
          pages.push('...')
        }
        
        pages.push(lastPage)
      }
      
      return pages
    }

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === lastPage}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{((currentPage - 1) * perPage) + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * perPage, totalInvoices)}
              </span>{' '}
              of <span className="font-medium">{totalInvoices}</span> results
            </p>
          </div>
          <div className="flex items-center gap-2">
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-1.5 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed dark:ring-gray-600 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="relative inline-flex items-center px-4 py-1.5 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0 dark:text-gray-300 dark:ring-gray-600"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-3 py-1.5 text-xs font-semibold ${
                      currentPage === page
                        ? 'z-10 bg-[#171717] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                )
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === lastPage}
                className="relative inline-flex items-center rounded-r-md px-1.5 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed dark:ring-gray-600 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
            
            <div className="flex items-center gap-2 ml-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">Go to</span>
              <input
                type="number"
                min="1"
                max={lastPage}
                className="w-16 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const page = Number(e.target.value)
                    if (page >= 1 && page <= lastPage) {
                      handlePageChange(page)
                    }
                  }
                }}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">of {lastPage}</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('invoices.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('invoices.subtitle')}</p>
          </div>
          {can('invoices.create') && (
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-4 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
            >
              <Plus className="w-4 h-4" />
              {t('invoices.new_invoice')}
            </button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <span>{t('invoices.client_name')}</span>
                  <input
                    value={clientName}
                    onChange={(event) => setClientName(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder={t('invoices.client_name.placeholder')}
                  />
                  {errors.clientName && <p className="text-xs text-red-600">{errors.clientName}</p>}
                </label>
                <label className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <span>{t('invoices.client_phone')}</span>
                  <input
                    value={clientPhone}
                    onChange={(event) => setClientPhone(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder={t('invoices.client_phone.placeholder')}
                  />
                  {errors.clientPhone && <p className="text-xs text-red-600">{errors.clientPhone}</p>}
                </label>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <span>{t('invoices.status')}</span>
                  <select
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm text-gray-900 outline-none transition focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="not_yet">{t('invoices.status.not_yet')}</option>
                    <option value="regle">{t('invoices.status.regle')}</option>
                  </select>
                </label>
                <label className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <span>{t('invoices.remise')}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={remise}
                    onChange={(e) => setRemise(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="0.00"
                  />
                </label>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setScanModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-gray-800"
                >
                  <ScanBarcode className="h-4 w-4" />
                  {t('invoices.scan_now')}
                </button>
              </div>

              <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
                  <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3">{t('invoices.product')}</th>
                      <th className="px-2 py-3">{t('invoices.variant')}</th>
                      <th className="px-2 py-3">{t('invoices.quantity')}</th>
                      <th className="px-2 py-3">{t('invoices.unit_price')}</th>
                      <th className="px-2 py-3">{t('invoices.line_total')}</th>
                      <th className="px-2 py-3">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const product = products.find((product) => product.id === Number(item.product_id))
                      const variants = product?.variants || []

                      return (
                        <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-3 relative align-top">
                            <ProductDropdown index={index} item={item} />
                            {errors.items?.[index]?.product && <p className="text-xs text-red-600">{errors.items[index].product}</p>}
                          </td>
                          <td className="px-2 py-3 align-top">
                            {variants.length > 0 ? (
                              <select
                                value={item.product_variant_id}
                                onChange={(event) => handleVariantChange(index, event.target.value)}
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                              >
                                <option value="">{t('invoices.select_variant')}</option>
                                {variants.map((variant) => (
                                  <option key={variant.id} value={variant.id}>
                                    {variant.variant_reference || `${variant.color || ''}${variant.color && variant.size ? ' / ' : ''}${variant.size || ''}`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">{t('invoices.no_variant')}</span>
                            )}
                            {errors.items?.[index]?.variant && <p className="text-xs text-red-600">{errors.items[index].variant}</p>}
                          </td>
                          <td className="px-2 py-3 align-top w-[10%]">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(event) => handleQuantityChange(index, event.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                            />
                            {errors.items?.[index]?.quantity && <p className="text-xs text-red-600">{errors.items[index].quantity}</p>}
                          </td>
                          <td className="px-2 py-3 align-top w-[13%]">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(event) => handleUnitPriceChange(index, event.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                              placeholder={product ? String(product.price) : t('invoices.product_price')}
                            />
                          </td>
                          <td className="px-2 py-5 align-top font-semibold">
                            {item.line_total}
                          </td>
                          <td className="px-2 py-3 align-top text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(index)}
                              className="inline-flex items-center justify-center rounded-lg border border-red-300 p-2 text-xs text-red-600 transition hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
                            >
                              <Trash className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-4 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
                >
                  <Plus className="w-4 h-4" />
                  {t('invoices.add_item')}
                </button>
                <div className="rounded-lg bg-gray-50 px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                  {t('invoices.total')}: {invoiceTotal.toFixed(2)}
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleSaveInvoice}
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-lg bg-[#171717] px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {saving ? 
                  <div className='flex items-center gap-1'>
                    <Loading size={5} />
                    {t('invoices.saving')}
                  </div>
                  : 
                  t('invoices.save')}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="hidden inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-900 transition hover:bg-gray-50 disabled:opacity-70 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                >
                  <Printer className="mr-2 w-4 h-4" />
                  {t('invoices.print')}
                </button>
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.preview_title')}</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('invoices.preview_description')}</p>
              <div className="mt-4 grid gap-3 text-sm text-gray-700 dark:text-gray-300">
                <div>
                  <span className="font-semibold">{t('invoices.client_name')}:</span> {clientName || '—'}
                </div>
                <div>
                  <span className="font-semibold">{t('invoices.client_phone')}:</span> {clientPhone || '—'}
                </div>
                <div>
                  <span className="font-semibold">{t('invoices.status')}:</span> {t(`invoices.status.${savedInvoice?.status || status}`)}
                </div>
                <div>
                  <span className="font-semibold">{t('invoices.items_count')}:</span> {items.length}
                </div>
                <div>
                  <span className="font-semibold">{t('invoices.subtotal')}:</span> {invoiceSubtotal.toFixed(2)}
                </div>
                {Number(remise) > 0 && (
                  <div>
                    <span className="font-semibold">{t('invoices.remise')}:</span> -{Number(remise).toFixed(2)}
                  </div>
                )}
                <div>
                  <span className="font-semibold">{t('invoices.total')}:</span> {invoiceTotal.toFixed(2)}
                </div>
              </div>
            </div>

            {savedInvoice && (
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.saved_invoice')}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{savedInvoice.invoice_code}</p>
                    {savedInvoice.remise > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('invoices.remise')}: -{Number(savedInvoice.remise).toFixed(2)}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="hidden inline-flex items-center gap-2 rounded-lg bg-[#171717] px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
                  >
                    <Printer className="w-4 h-4" />
                    {t('invoices.print')}
                  </button>
                </div>
                {savedInvoice.client_history && (
                  <div className="mt-4 space-y-4 border-t border-gray-200 pt-4 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{t('invoices.client_history.title')}</h3>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('invoices.client_history.total_invoiced')}</div>
                        <div className="font-semibold">{Number(savedInvoice.client_history.total_invoiced || 0).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('invoices.client_history.total_paid')}</div>
                        <div className="font-semibold">{Number(savedInvoice.client_history.total_paid || 0).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('invoices.client_history.balance')}</div>
                        <div className="font-semibold">{Number(savedInvoice.client_history.balance || 0).toFixed(2)}</div>
                      </div>
                    </div>
                    <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('invoices.client_history.invoices')}</div>
                      {savedInvoice.client_history.invoices.length > 0 ? (
                        <ul className="mt-2 space-y-2 text-sm max-h-48 overflow-y-auto">
                          {savedInvoice.client_history.invoices.map((invoice) => (
                            <li key={invoice.id} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                              <div className="font-semibold">{invoice.invoice_code}</div>
                              <div>{t('invoices.status')}: {t(`invoices.status.${invoice.status}`)}</div>
                              <div>{t('invoices.total')}: {Number(invoice.total).toFixed(2)}</div>
                              <div>{new Date(invoice.created_at).toLocaleDateString()}</div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('invoices.client_history.no_invoices')}</p>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('invoices.client_history.payments')}</div>
                      {savedInvoice.client_history.payments.length > 0 ? (
                        <ul className="mt-2 space-y-2 text-sm max-h-48 overflow-y-auto">
                          {savedInvoice.client_history.payments.map((payment) => (
                            <li key={payment.id} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                              <div>{t('invoices.client_history.payment_amount')}: {Number(payment.amount).toFixed(2)}</div>
                              <div>{t('invoices.client_history.payment_date')}: {new Date(payment.payment_date).toLocaleDateString()}</div>
                              {payment.note && <div>{t('invoices.client_history.payment_note')}: {payment.note}</div>}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('invoices.client_history.no_payments')}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>

        <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4'>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.all_invoices')}</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={t('clients.search_placeholder')}
                  className="w-full sm:w-64 rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <select
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3">{t('invoices.invoice_code')}</th>
                  <th className="px-4 py-3">{t('invoices.client_name')}</th>
                  <th className="px-4 py-3">{t('invoices.total')}</th>
                  <th className="px-4 py-3">{t('invoices.remise')}</th>
                  <th className="px-4 py-3">{t('invoices.price_paye')}</th>
                  <th className="px-4 py-3">{t('invoices.balance')}</th>
                  <th className="px-4 py-3">{t('invoices.status')}</th>
                  <th className="px-4 py-3">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {loadingInvoices ? (
                  <tr>
                    <td colSpan="100" className="text-center py-4">
                        <div className="flex justify-center">
                          <Loading size={10} />
                        </div>
                      </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-6 text-center text-gray-500">{t('invoices.no_invoices')}</td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-4 text-xs py-3">
                        {invoice.invoice_code}
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">
                            {new Date(invoice.created_at).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                            })}
                          </div>
                      </td>
                      <td className="px-4 py-3">
                        {invoice.client_name}
                        {invoice.client_phone && <span className="block text-xs text-gray-500 dark:text-gray-400">{invoice.client_phone}</span>}
                      </td>
                      <td className="px-4 py-3">{Number(invoice.total).toFixed(2)}</td>
                      <td className="px-4 py-3">{invoice.remise > 0 ? lang === "ar" ? `${Number(invoice.remise).toFixed(2)}-` : `-${Number(invoice.remise).toFixed(2)}` : '—'}</td>
                      <td className="px-4 py-3">{Number(invoice.paid).toFixed(2)}</td>
                      <td className="px-4 py-3">{Number(invoice.balance).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          invoice.status === 'regle' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'
                        }`}>
                          {t(`invoices.status.${invoice.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right flex gap-2 justify-end">
                        {can('invoices.view') && (
                          <button
                            type="button"
                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                          >
                            <FileText className="w-4 h-4" />
                            {t('invoices.view')}
                          </button>
                        )}
                        {can('invoices.edit') && (
                          <button
                            type="button"
                            onClick={() => setEditModalInvoice(invoice)}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                          >
                            <Edit className="w-4 h-4" />
                            {t('invoices.edit')}
                          </button>
                        )}
                        {can('invoices.pay') && (
                          <button
                            type="button"
                            onClick={() => handleAddPayment(invoice)}
                            disabled={invoice.balance <= 0}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#171717] px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PlusCircle className="w-4 h-4" />
                            {t('invoices.add_payment')}
                          </button>
                        )}
                        {can('invoices.view') && (
                          <button
                            type="button"
                            onClick={() => handleViewHistory(invoice)}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                          >
                            <Eye className="w-4 h-4" />
                            {t('invoices.history')}
                          </button>
                        )}
                        {can('invoices.delete') && (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirmInvoice(invoice)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {!loadingInvoices && invoices.length > 0 && <PaginationControls />}
        </div>

        {/* Payment Modal */}
        {paymentModalOpen && selectedInvoice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900 w-full max-w-md">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.add_payment_form')}</h3>
                <button
                  type="button"
                  onClick={() => setPaymentModalOpen(false)}
                  className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('invoices.invoice_code')}: {selectedInvoice.invoice_code}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedInvoice.client_name}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">
                    {t('invoices.total')}: {Number(selectedInvoice.total).toFixed(2)}
                  </label>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">
                    {t('invoices.price_paye')}: {Number(selectedInvoice.paid).toFixed(2)}
                  </label>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">
                    {t('invoices.balance')}: {Number(selectedInvoice.balance).toFixed(2)}
                  </label>
                </div>
                <label className="block">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('invoices.payment_amount')}</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="0.00"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('invoices.payment_note')}</span>
                  <input
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder={t('invoices.payment_note_placeholder')}
                  />
                </label>
                <div className="flex gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setPaymentModalOpen(false)}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                  >
                    {t('invoices.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitPayment}
                    disabled={savingPayment}
                    className="flex-1 rounded-lg bg-[#171717] px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {
                      savingPayment ? (
                        <div className="flex items-center gap-0.5 justify-center">
                          <Loading size={5} />
                          <span>{t('invoices.payment_saving')}</span>
                        </div>
                      ) : (
                        t('invoices.payment_submit')
                      )
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {historyModalOpen && selectedInvoiceHistory && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900 w-full max-w-2xl max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.payment_history')}</h3>
                <button
                  type="button"
                  onClick={() => setHistoryModalOpen(false)}
                  className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                {(selectedInvoiceHistory.payments || []).length > 0 ? (
                  (selectedInvoiceHistory.payments || []).map((payment, index) => (
                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('invoices.payment_amount')}: {Number(payment.amount).toFixed(2)}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{new Date(payment.payment_date).toLocaleDateString()}</p>
                        </div>
                        {can('invoices.pay') && (
                          <button
                            type="button"
                            onClick={() => setPaymentToRemove(payment)}
                            className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="w-3 h-3" />
                            {t('invoices.remove_payment')}
                          </button>
                        )}
                      </div>
                      {payment.note && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{payment.note}</p>}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('invoices.no_payments')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {paymentToRemove && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">{t('invoices.remove_payment')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('invoices.remove_payment_confirm')}</p>
              <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-50">{t('invoices.payment_amount')}: {Number(paymentToRemove.amount).toFixed(2)}</p>
              <div className="flex gap-2 pt-6">
                <button
                  type="button"
                  onClick={() => setPaymentToRemove(null)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                >
                  {t('invoices.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleRemovePayment}
                  disabled={removingPayment}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                {
                  removingPayment ? (
                    <div className="flex items-center gap-0.5 justify-center">
                      <Loading size={5} />
                      <span>{t('invoices.deleting')}</span>
                    </div>
                  ) : (
                    t('invoices.yes_remove')
                  )
                }
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="hidden mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900" ref={printRef}>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">{t('invoices.preview_title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('invoices.preview_description')}</p>
            </div>
            <div className="space-y-1 text-right text-sm text-gray-700 dark:text-gray-300">
              <div>{t('invoices.invoice_code')}: {savedInvoice?.invoice_code || invoiceCode || t('invoices.invoice_code.draft')}</div>
              <div>{t('invoices.client_name')}: {savedInvoice?.client_name || clientName || '—'}</div>
              <div>{t('invoices.total')}: {Number(savedInvoice?.total ?? invoiceTotal).toFixed(2)}</div>
            </div>
          </div>

          <div className="mt-4">
            <img
              src={logo}
              alt="Logo"
              className="h-12 object-contain"
            />
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3">{t('invoices.product')}</th>
                  <th className="px-4 py-3">{t('invoices.variant')}</th>
                  <th className="px-4 py-3">{t('invoices.quantity')}</th>
                  <th className="px-4 py-3">{t('invoices.unit_price')}</th>
                  <th className="px-4 py-3">{t('invoices.line_total')}</th>
                </tr>
              </thead>
              <tbody>
                {(savedInvoice ? savedInvoice.items : items).map((item, index) => (
                  <tr key={item.id ?? index} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3">{item.product_name}</td>
                    <td className="px-4 py-3">{item.variant_reference || '—'}</td>
                    <td className="px-4 py-3">{item.quantity}</td>
                    <td className="px-4 py-3">{Number(item.unit_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">{Number(item.line_total || (item.quantity * item.unit_price) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editModalInvoice && (
        <InvoiceEditModal
          invoice={editModalInvoice}
          products={products}
          onClose={() => setEditModalInvoice(null)}
          onUpdated={() => { fetchInvoices(currentPage, perPage); fetchProducts(); }}
        />
      )}

      {scanModalOpen && (
        <InvoiceScanModal
          products={products}
          onAddItem={handleAddScannedItem}
          onClose={() => setScanModalOpen(false)}
        />
      )}

      {deleteConfirmInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">{t('invoices.delete')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('invoices.delete_confirm')}</p>
            <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-50">{deleteConfirmInvoice.invoice_code} — {deleteConfirmInvoice.client_name}</p>
            <div className="flex gap-2 pt-6">
              <button
                type="button"
                onClick={() => setDeleteConfirmInvoice(null)}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
              >
                {t('invoices.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteInvoice}
                disabled={deletingInvoice}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {deletingInvoice ? t('invoices.deleting') : t('invoices.yes_delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}